/**
 * LegalPage — les quatre documents juridiques, une seule page.
 *
 * /conditions-generales · /confidentialite · /cookies · /mentions-legales
 *
 * Publiques, indexables, accessibles sans connexion. Le document est
 * choisi par le `slug` passé par la route (App.js) : un seul composant,
 * une entrée de contenu par document (content/legal/index.js) — même
 * organisation que LandingThemePage et BePillarPage.
 *
 * Le contenu n'est PAS réécrit ici : il vient tel quel de
 * content/legal/*.{fr,nl,en}.js et passe par lib/legalMarkdown.jsx. Cette
 * page ne décide que de la mise en page.
 *
 * TROIS LANGUES. La page suit le sélecteur du header, comme le reste du
 * site public : les quatre documents existent en français, en néerlandais
 * et en anglais. La ROUTE ne change pas — `/conditions-generales` sert la
 * version néerlandaise à un visiteur en NL. C'est la version française
 * qui prévaut en cas de divergence, et chaque document le dit en tête.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHead from '../components/seo/PageHead';
import { generateMetadata, SITE_URL } from '../lib/seo/metadata';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { getLegalDoc, docsFor, LEGAL_SLUGS, DATE_MISE_EN_LIGNE } from '../content/legal';
import { renderLegalDocument } from '../lib/legalMarkdown';
import '../styles/legal.css';

// Chrome de la page — trois libellés qui n'appartiennent à aucun des
// quatre documents. Copie locale, comme ailleurs dans le repo pour des
// chaînes qui ne servent qu'ici.
const COPY = {
  fr: { eyebrow: 'Informations légales', toc: 'Sommaire', version: 'Version du' },
  nl: { eyebrow: 'Juridische informatie', toc: 'Inhoudsopgave', version: 'Versie van' },
  en: { eyebrow: 'Legal information', toc: 'Contents', version: 'Version of' },
};

export default function LegalPage({ slug }) {
  const [lang] = useSiteLanguage();
  const doc = getLegalDoc(slug, lang);
  const docs = docsFor(lang);
  const c = COPY[lang] || COPY.fr;

  // Le rendu markdown est pur : il ne dépend que du document rendu, donc
  // il ne se refait qu'au changement de langue ou de slug.
  const rendered = useMemo(
    () => (doc ? renderLegalDocument(doc.markdown) : { nodes: [], toc: [] }),
    [doc],
  );

  // Une route inconnue ne devrait pas exister (les quatre sont déclarées
  // en dur dans App.js), mais un composant public ne plante pas : il rend
  // le strict minimum et laisse le chrome debout.
  if (!doc) return null;

  const metadata = generateMetadata({
    title: doc.title,
    description: doc.description,
    canonical: `${SITE_URL}/${doc.slug}`,
    // Une seule URL pour les trois langues : la canonique et les trois
    // alternantes pointent donc toutes sur elle. C'est la description
    // honnête de la situation — le document est servi dans la langue du
    // visiteur, à la même adresse — plutôt que trois URL inventées qui
    // renverraient un 404.
    alternates: {
      fr: `${SITE_URL}/${doc.slug}`,
      nl: `${SITE_URL}/${doc.slug}`,
      en: `${SITE_URL}/${doc.slug}`,
    },
    locale: `${doc.lang}_BE`,
  });

  const others = LEGAL_SLUGS.filter((s) => s !== doc.slug);

  // Le sommaire se glisse JUSTE AVANT la première section numérotée,
  // c'est-à-dire après le titre, la ligne « dernière mise à jour » et le
  // filet. Le poser avant tout ça donnerait une page qui s'ouvre sur une
  // liste de liens sans avoir dit ce qu'elle est ; le poser plus bas le
  // rendrait invisible.
  const firstSection = rendered.nodes.findIndex((n) => n?.props?.className === 'legal-h2');
  const tocAt = firstSection === -1 ? rendered.nodes.length : firstSection;

  return (
    <div className="legal-page" data-testid={`legal-page-${doc.slug}`}>
      <PageHead metadata={metadata} />
      <PublicHeader />

      <main className="legal-shell">
        <p className="legal-eyebrow">{c.eyebrow}</p>

        {/* Titre, mention de version, filet. */}
        {rendered.nodes.slice(0, tocAt)}

        {/* Table des matières — CGV uniquement : quatorze sections, c'est
            le seul des quatre documents qui se parcourt plutôt qu'il ne
            se lit. Ancres natives : `scroll-margin-top` dégage la barre
            fixe (cf. legal.css). */}
        {doc.toc && rendered.toc.length > 0 && (
          <nav className="legal-toc" aria-label={c.toc} data-testid="legal-toc">
            <div className="legal-toc-title">{c.toc}</div>
            <ul className="legal-toc-list">
              {rendered.toc.map((entry) => (
                <li key={entry.id}>
                  <a href={`#${entry.id}`}>{entry.text}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {rendered.nodes.slice(tocAt)}

        <div className="legal-crosslinks">
          {others.map((s) => (
            <Link key={s} to={`/${s}`}>{docs[s].label}</Link>
          ))}
          <span style={{ fontSize: 13.5, color: '#9ca3af' }}>
            {c.version} {DATE_MISE_EN_LIGNE}
          </span>
        </div>
      </main>

      <SiteFooterV2 />
    </div>
  );
}
